/**
 * @file UsageSection.tsx
 * @brief Billing → Usage: where this period stands. One pace card for the
 *        main allowance (a timeline of the period with today, used and where
 *        today's rate ends up), then a row of the smaller numbers.
 *
 * @details
 * The period is the server's (billingPeriod.ts): the plan's minutes window
 * (`usage.periodStart/End`, never the annual renewal date), or with no plan
 * the UTC month the server counts Asks in. Spend is the invoices feed over that
 * same window minus plan charges.
 * Since BACKEND A3 (18-10-2026) the server reports each pool (`usage.pools`):
 * Intelligence always, Meeting Notes only while its own pool is on (ADR-030),
 * included interpretation on Notes plans; and a `period` for users with no plan.
 * The pace (DES 21-10-2026) is worked out here from used, included and the
 * window (`usagePace`); the server sends no forecast.
 *
 * @author DES
 * @date 17-10-2026
 * @updated 21-10-2026 — period-pace layout (DES)
 */

import { Link } from "react-router-dom";
import { CalendarClock, Gauge, MessageCircleQuestion, NotebookPen, Receipt, Sparkles, type LucideIcon } from "lucide-react";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { GridBand, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { useMyAskQuota, useMyInvoices, useMySubscription } from "../api";
import {
  fairUseHours,
  fairUseReachedMessage,
  fairUseState,
  FREE_INTEL_ON_PRO_MESSAGE,
  freeIntelUsedUp,
  freeMinutesLine,
  freeMinutesState,
  freePoolTitle,
  freeResetsMessage,
  freeUsedUpMessage,
  isOneTime,
  resetDayLabel,
} from "../freeMinutes";
import { billingPath } from "../billingPaths";
import { countryBlockedMessage } from "../billingErrors";
import {
  minutesBar,
  paygSpend,
  periodLabel,
  periodQuery,
  resolveBillingPeriod,
  usagePace,
  type BillingPeriod,
  type MinutesBar,
  type UsagePace,
} from "../billingPeriod";
import { overageSoFar } from "../planCompare";
import { isServerActive } from "../planConfirm";

const fmt = (v: number) => v.toLocaleString("en-GB");
const shortDay = (d: Date, timeZone?: string) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone }).format(d);

const CHIP: Record<UsagePace["status"], { text: string; className: string }> = {
  idle: { text: "Not started", className: "bg-foreground/[0.07] text-muted-foreground" },
  early: { text: "Just started", className: "bg-foreground/[0.07] text-muted-foreground" },
  on_track: { text: "On track", className: "bg-success/15 text-success dark:text-green-300" },
  ahead: { text: "Ahead of pace", className: "bg-warning/15 text-warning dark:text-amber-300" },
  over: { text: "Over the allowance", className: "bg-destructive/15 text-destructive dark:text-red-300" },
};

/** The main allowance against the calendar: the period as a line, today on it. */
function PaceCard({
  label,
  icon: Icon,
  bar,
  period,
  overage,
  overNote,
}: {
  label: string;
  icon: LucideIcon;
  bar: MinutesBar;
  period: BillingPeriod;
  overage?: { minutes: number; cents: number | null; currency: string } | null;
  /** What happens past the allowance, for the "ahead" line. */
  overNote: string;
}) {
  const tz = period.source === "plan" ? undefined : "UTC";
  const pace = usagePace(bar.used, bar.included, period);
  const lastDay = new Date(period.end.getTime() - 1);
  const status = pace?.status ?? "idle";
  const chip = CHIP[status];
  const projected = pace?.projectedUsed != null ? Math.min(1, pace.projectedUsed / bar.included) : null;
  const overMinutes = overage?.minutes ?? bar.over;

  const line =
    status === "over"
      ? `${fmt(overMinutes)} min over${overage?.cents != null ? `, ${formatMoneyMinor(overage.cents, overage.currency)} so far` : ""}. ${overNote}`
      : status === "ahead" && pace?.runOutAt
        ? `At this pace your minutes run out around ${shortDay(pace.runOutAt, tz)}. ${overNote}`
        : status === "on_track" && pace?.projectedUsed != null
          ? `At this pace you'll use about ${fmt(pace.projectedUsed)} of ${fmt(bar.included)} by ${shortDay(lastDay, tz)}.`
          : status === "early"
            ? "Too early to read your pace. Check back in a day or two."
            : `Nothing used yet. Your minutes reset on ${shortDay(period.end, tz)}.`;

  return (
    <GridCell className="col-span-full flex flex-col gap-5 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-brand-soft text-brand-soft-foreground">
            <Icon className="size-4" strokeWidth={1.75} />
          </span>
          <p className="text-sm font-medium">{label}</p>
        </div>
        <span className={cn("rounded-full px-3 py-1 text-xs font-medium", chip.className)}>{chip.text}</span>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <p className="flex items-baseline gap-2">
          <span className="text-4xl font-semibold tracking-tight tabular-nums">{fmt(bar.left)}</span>
          <span className="text-sm text-muted-foreground">min left of {fmt(bar.included)}</span>
        </p>
        {pace ? (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarClock className="size-4" strokeWidth={1.75} />
            Day {pace.day} of {pace.totalDays} · {pace.daysLeft} {pace.daysLeft === 1 ? "day" : "days"} left
          </p>
        ) : null}
      </div>

      {/* The period as a line: used (solid), where today's rate ends up
          (faint), and today. */}
      <div className="pt-5">
        <div className="relative h-3 rounded-full bg-foreground/[0.08]" aria-hidden>
          {projected != null && projected > bar.fraction ? (
            <div
              className={cn("absolute inset-y-0 left-0 rounded-full", status === "ahead" ? "bg-warning/30" : "bg-brand/25")}
              style={{ width: `${projected * 100}%` }}
            />
          ) : null}
          <div
            className={cn("absolute inset-y-0 left-0 rounded-full", status === "over" ? "bg-destructive" : "bg-brand")}
            style={{ width: `${Math.max(bar.fraction * 100, bar.used > 0 ? 1.5 : 0)}%` }}
          />
          {pace ? (
            <div className="absolute -top-1.5 -translate-x-1/2" style={{ left: `${pace.timeFraction * 100}%` }}>
              <span className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium text-foreground">
                Today
              </span>
              <div className="h-6 w-0.5 rounded-full bg-foreground" />
            </div>
          ) : null}
        </div>
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>{shortDay(period.start, tz)}</span>
          <span>{fmt(bar.used)} used</span>
          <span>{shortDay(lastDay, tz)}</span>
        </div>
      </div>

      <p className={cn("text-sm", status === "ahead" || status === "over" ? "text-foreground" : "text-muted-foreground")}>{line}</p>
    </GridCell>
  );
}

/** One of the smaller numbers under the pace card. */
function MiniStat({
  icon: Icon,
  label,
  value,
  sub,
  bar,
  children,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  bar?: number | null;
  children?: React.ReactNode;
}) {
  return (
    <GridCell className="flex flex-col gap-1.5 p-5">
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="size-4" strokeWidth={1.75} />
        {label}
      </p>
      <p className="text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
      {bar != null ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/[0.08]" aria-hidden>
          <div className="h-full rounded-full bg-brand" style={{ width: `${Math.max(bar * 100, bar > 0 ? 2 : 0)}%` }} />
        </div>
      ) : null}
      {sub ? <p className="text-xs text-muted-foreground">{sub}</p> : null}
      {children}
    </GridCell>
  );
}

export function UsageSection() {
  const status = useMySubscription();
  const quota = useMyAskQuota();
  const sub = status.data?.subscription;
  const usage = isServerActive(sub?.status) ? status.data?.usage : null;
  const period = resolveBillingPeriod(usage, new Date(), status.data?.period);
  const q = periodQuery(period);
  const invoices = useMyInvoices({ page: 1, pageSize: 1, from: q.from, to: q.to });

  if (status.isLoading) return <SkeletonRows rows={3} twoLine />;

  const bar = usage ? minutesBar(usage.usedMinutes, usage.includedMinutes) : null;
  const pools = usage?.pools ?? null;
  const plan = status.data?.catalog.find((p) => p.code === sub?.planCode);
  const currency = plan?.currency ?? status.data?.currency ?? "GBP";
  const ask = quota.data?.quota;
  const spend = paygSpend(invoices.data);
  const totals = invoices.data?.totalsByCurrency ?? [];
  const free = status.data?.freePlan ?? null;
  // One-time Free (B20): a total, once; no month to pace against.
  const freeOnce = isOneTime(status.data?.freeMinutes) ? status.data!.freeMinutes! : null;

  const intelBar = bar ? (pools?.intelligence ? minutesBar(pools.intelligence.used, pools.intelligence.included) ?? bar : bar) : null;
  const notesBar = pools?.notes ? minutesBar(pools.notes.used, pools.notes.included) : null;
  // Pro / Business fair use (B6, 22-10-2026): out of sight under 80%, then
  // hours. Null (Enterprise, backend before B6) keeps the Notes pool tile.
  const fairUse = status.data?.fairUse ?? null;
  const fairUseShown = fairUseState(fairUse);
  // Blocked country (B1): no free minutes and no pay as you go. Its message
  // replaces the Free tiles, never "0 of 0".
  const blocked = countryBlockedMessage(status.data);
  const freeNotesBar = free && !free.notAvailable ? minutesBar(free.notesUsed, free.notesMinutes) : null;
  const interpBar = pools?.interpretation ? minutesBar(pools.interpretation.used, pools.interpretation.included) : null;

  // Over the allowance so far, for the next invoice (ADR-045): the server
  // sends no field yet, so minutes over × the plan's rate.
  const overage = (() => {
    const o = overageSoFar(pools?.intelligence ?? (usage ? { used: usage.usedMinutes, included: usage.includedMinutes } : null), plan?.overageRateMinor);
    return o ? { ...o, currency } : null;
  })();

  // Ask: limited (used of limit), unlimited on the plan, or off.
  const askValue =
    quota.data?.enabled === false
      ? "Off"
      : ask?.enforced
        ? `${fmt(Math.max(0, ask.limit - ask.used))} left`
        : ask || (plan && plan.askLimit == null)
          ? "Unlimited"
          : quota.isLoading
            ? "…"
            : "Unlimited";
  const askSub =
    quota.data?.enabled === false
      ? "Ask AI is turned off."
      : ask?.enforced
        ? `${fmt(ask.used)} of ${fmt(ask.limit)} asked${ask.resetMode === "one_time" ? ", once" : ""}`
        : ask
          ? `${fmt(ask.used)} asked this period`
          : "Questions about your meetings";

  const spendValue = invoices.isLoading
    ? "…"
    : spend
      ? formatMoneyMinor(spend.cents, spend.currency)
      : totals.length > 1
        ? totals.map((t) => formatMoneyMinor(t.amountCents, t.currency)).join(" + ")
        : formatMoneyMinor(0, currency);

  return (
    <>
      <GridBand>
        <h3 className="text-base font-semibold tracking-tight">This period</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {periodLabel(period)}
          {period.source === "plan" ? " · your plan's minutes window" : " · calendar month (UTC)"}
        </p>
      </GridBand>

      <GridCells className="sm:grid-cols-2 lg:grid-cols-3">
        {intelBar ? (
          <PaceCard
            label="Intelligence minutes"
            icon={Sparkles}
            bar={intelBar}
            period={period}
            overage={overage}
            overNote={
              plan?.overageRateMinor != null
                ? "Extra minutes go on your next invoice."
                : "Past the allowance, minutes are charged pay as you go."
            }
          />
        ) : freeOnce ? (
          <GridCell className="col-span-full flex flex-col gap-2 p-5 md:p-6">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-lg bg-brand-soft text-brand-soft-foreground">
                <NotebookPen className="size-4" strokeWidth={1.75} />
              </span>
              <p className="text-sm font-medium">
                {/* B43: one pool for Meeting Notes or Full Intelligence. */}
                {freePoolTitle(freeOnce) ?? "Free minutes (one time)"}
              </p>
            </div>
            <p className="text-2xl font-semibold tracking-tight tabular-nums">{freeMinutesLine(freeOnce)}</p>
            <p className="text-sm text-muted-foreground">
              {freeMinutesState(freeOnce) === "blocked"
                ? freeUsedUpMessage(freeOnce)
                : freeIntelUsedUp(freeOnce)
                  ? FREE_INTEL_ON_PRO_MESSAGE
                  : "They don't reset. No card needed, never charged."}
            </p>
          </GridCell>
        ) : freeNotesBar ? (
          <PaceCard
            label="Meeting Notes minutes (Free)"
            icon={NotebookPen}
            bar={freeNotesBar}
            period={period}
            // B4 (22-10-2026): Free has no overage; new recordings wait for the reset.
            overNote={
              freeResetsMessage(status.data?.freeMinutes ?? null) ?? "New recordings start again next month."
            }
          />
        ) : (
          <GridCell className="col-span-full flex flex-wrap items-center justify-between gap-4 p-5 md:p-6">
            <div>
              <p className="text-sm font-medium">{blocked ? "Not available in your country" : "No plan"}</p>
              <p className="mt-1 text-sm text-muted-foreground">{blocked ?? "Meetings and notes are charged per minute."}</p>
            </div>
            {blocked ? null : (
              <Link to={billingPath("plan")} className="text-sm font-medium underline underline-offset-2">
                See plans
              </Link>
            )}
          </GridCell>
        )}

        {fairUse ? (
          fairUseShown ? (
            <MiniStat
              icon={NotebookPen}
              label="Meeting Notes this month"
              value={fairUseHours(fairUse) ?? ""}
              bar={Math.min(1, fairUse.used / fairUse.limit)}
              sub={
                fairUseShown === "blocked"
                  ? fairUseReachedMessage(fairUse)
                  : `Fair use. Resets ${resetDayLabel(fairUse.resetsAt) ?? "with your plan"}.`
              }
            />
          ) : null
        ) : notesBar ? (
          <MiniStat
            icon={NotebookPen}
            label="Meeting Notes"
            value={`${fmt(notesBar.left)} left`}
            bar={notesBar.fraction}
            sub={`${fmt(notesBar.used)} of ${fmt(notesBar.included)} used, under fair use`}
          />
        ) : intelBar && freeNotesBar ? (
          <MiniStat
            icon={NotebookPen}
            label="Meeting Notes (Free)"
            value={`${fmt(freeNotesBar.left)} left`}
            bar={freeNotesBar.fraction}
            sub={`${fmt(freeNotesBar.used)} of ${fmt(freeNotesBar.included)} used`}
          />
        ) : null}

        {interpBar ? (
          <MiniStat
            icon={Gauge}
            label="Interpretation"
            value={`${fmt(interpBar.left)} left`}
            bar={interpBar.fraction}
            sub={`${fmt(interpBar.used)} of ${fmt(interpBar.included)} used. Past it, sessions are charged per minute.`}
          />
        ) : plan?.includedInterpretationMinutes ? (
          <MiniStat
            icon={Gauge}
            label="Interpretation"
            value={`${fmt(plan.includedInterpretationMinutes)} included`}
            sub="Minutes used aren't shown here yet."
          />
        ) : null}

        <MiniStat
          icon={MessageCircleQuestion}
          label="Ask AI"
          value={askValue}
          bar={ask?.enforced && ask.limit > 0 ? Math.min(1, ask.used / ask.limit) : null}
          sub={askSub}
        />

        <MiniStat
          icon={Receipt}
          label="Pay as you go"
          value={spendValue}
          sub={
            totals.length > 1
              ? "In more than one currency; includes plan charges."
              : "Sessions, meetings and notes billed this period. Plan charges not included."
          }
        >
          <Link to={billingPath("invoices")} className="text-sm font-medium underline underline-offset-2">
            See invoices
          </Link>
        </MiniStat>
      </GridCells>
    </>
  );
}
